import { defineStore } from "pinia";
import { Component, computed, ref, shallowRef } from "vue";

import EmailVerificationApi from "@Modules/shared/Api/EmailVerificationApi";
import V2EmailVerifyPopover from "@Modules/shared/Components/V2EmailVerifyPopover.vue";
import V2NotificationsPopover from "@Modules/shared/Components/V2NotificationsPopover.vue";
import V2CreateFormPopover from "@Modules/urls/Components/V2CreateFormPopover.vue";
import V2EditFormPopover from "@Modules/urls/Components/V2EditFormPopover.vue";
import { UrlAlias } from "@Modules/urls/Data/Models/UrlAliasFactory";
import { UpdateUrlOptions } from "@Modules/urls/Api/UrlsApi";
import ShoppingCartPopover from "@Modules/shoppingcart/components/ShoppingCartPopover.vue";
import type { ComponentProps } from "@Modules/shared/interfaces/ComponentProps";

export enum Transitions {
  Pop = "pop",
  Fade = "fade",
  SlideLeft = "slide-left",
  SlideRight = "slide-right",
  None = "none",
}

/**
 * Manages which component is currently displayed
 * as a modal, popover, or popup across the application.
 *
 * The active component is rendered by `OverlayWrapper.vue`, which should be
 * mounted once at the application root. Only one overlay can be active at a time;
 */
const XOverlay = defineStore("x-overlay", () => {
  const activeComponent = shallowRef<Component | null>(null);
  const activeProps = ref<Record<string, unknown>>({});
  const activeEvents = ref<Record<string, unknown>>({});
  const activeTransition = ref<Transitions>(Transitions.Pop);

  function show<C extends Component>(
    component: C,
    props: ComponentProps<C> = {} as ComponentProps<C>,
    events: Record<string ,unknown> = {},
  ): void {
    hide();

    activeComponent.value = component;
    activeProps.value = props as Record<string, unknown>;
    activeEvents.value = events;
  }

  function hide(): void {
    activeComponent.value = null;
    activeProps.value = {};
    activeEvents.value = {};
  }

  function setTransition(transition: Transitions): void {
    activeTransition.value = transition;
  }

  function showCreateFormPopover() {
    show(V2CreateFormPopover, {}, {});
  }

  function showNotificationsPopover() {
    show(V2NotificationsPopover, {}, {});
  }

  function showEmailVerifyPopover() {
    show(V2EmailVerifyPopover, { resend: EmailVerificationApi().resendVerificationEmail }, { close: hide });
  }

  function showShoppingCartPopover() {
    show(ShoppingCartPopover, {}, {});
  }

  function showModal(
    component: Component,
    props: Record<string, unknown> = {},
    events: Record<string, unknown> = {},
  ): void {
    setTransition(Transitions.None);
    show(component, props, events);
  }

  function showEditFormPopover(
    urlAlias: UrlAlias,
    submit: (options: UpdateUrlOptions) => Promise<boolean>,
    errors: Record<string, string>,
    loading: boolean,
  ) {
    show(V2EditFormPopover, { urlAlias, submit, errors, loading }, {});
  }

  return {
    show,
    hide,
    setTransition,
    showCreateFormPopover,
    showNotificationsPopover,
    showEmailVerifyPopover,
    showShoppingCartPopover,
    showModal,
    showEditFormPopover,
    activeComponent: computed(() => activeComponent.value),
    props: computed(() => activeProps.value),
    events: computed(() => activeEvents.value),
    transition: computed(() => activeTransition.value),
  };
});

export default XOverlay;